import type { FC, ChangeEvent } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState, useCallback } from 'react';

import { Box } from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import { ChannelStepLayout } from 'src/pages/channel/shared';

import { Form, Field } from 'src/components/hook-form';

// ==================== Types ====================
interface LiveChatFormData {
  websiteName: string;
  websiteDomin: string;
  widgetColor: string;
  welcomeTitle: string;
  welcomeTagline: string;
}

// ==================== Constants ====================
const DEFAULT_WIDGET_COLOR = '#2563eb';

const FORM_FIELDS = [
  { name: 'websiteName', required: true },
  { name: 'websiteDomin', required: true },
  { name: 'widgetColor', required: false },
  { name: 'welcomeTitle', required: true },
  { name: 'welcomeTagline', required: true },
];

const DEFAULT_VALUES: LiveChatFormData = {
  websiteName: '',
  websiteDomin: '',
  widgetColor: '',
  welcomeTitle: '',
  welcomeTagline: '',
};

// ==================== Component ====================
const LiveChatStep1: FC = () => {
  const { t } = useTranslate('translation');
  const { state: channelState, handleLiveChatChannelDtls, onChangeStep } = useChannelContext();

  const liveChatDetail = channelState.LiveChatDeatil || DEFAULT_VALUES;
  const [color, setColor] = useState<string>(liveChatDetail.widgetColor || DEFAULT_WIDGET_COLOR);

  const schema = useMemo(() => validationSchema(FORM_FIELDS, t), [t]);

  const methods = useForm<LiveChatFormData>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
    values: liveChatDetail,
  });

  const { handleSubmit, watch, setValue } = methods;

  const [websiteName, websiteDomin, welcomeTagline, welcomeTitle] = watch([
    'websiteName',
    'websiteDomin',
    'welcomeTagline',
    'welcomeTitle',
  ]);

  const isFormValid = useMemo(
    () => Boolean(websiteName && websiteDomin && welcomeTitle && welcomeTagline),
    [websiteName, websiteDomin, welcomeTitle, welcomeTagline]
  );

  const handleColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newColor = e.target.value || DEFAULT_WIDGET_COLOR;
      setColor(newColor);
      setValue('widgetColor', newColor);
    },
    [setValue]
  );

  const onSubmit = handleSubmit(async (data: any) => {
    handleLiveChatChannelDtls(data);
    onChangeStep('next');
  });

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <ChannelStepLayout title="" isNextDisabled={!isFormValid}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Field.Text
            name="websiteName"
            label={t('WEB_NAME')}
            InputLabelProps={{ shrink: true }}
            placeholder={t('ENTER_WEBSITE_NAME')}
            fullWidth
          />

          <Field.Text
            name="websiteDomin"
            label={t('WEB_DOMAIN')}
            InputLabelProps={{ shrink: true }}
            placeholder={t('ENTER_WEBSITE_DOMAIN')}
            fullWidth
          />

          <Field.Text
            name="widgetColor"
            label={t('WIDGET_COLOR')}
            sx={{ width: '30%' }}
            slotProps={{ inputLabel: { shrink: true } }}
            type="color"
            value={color}
            onChange={(e) => {
              const newColor = e.target.value || '#000000';
              setColor(newColor);
              setValue('widgetColor', newColor);
            }}
          />

          <Field.Text
            name="welcomeTitle"
            label={t('WELL_HEADING')}
            InputLabelProps={{ shrink: true }}
            placeholder={t('ENTER_WELCOME_TITLE')}
            fullWidth
          />

          <Field.Text
            name="welcomeTagline"
            label={t('WELL_TAGLINE')}
            InputLabelProps={{ shrink: true }}
            placeholder={t('ENTER_WELCOME_TAGLINE')}
            fullWidth
          />
        </Box>
      </ChannelStepLayout>
    </Form>
  );
};

export default LiveChatStep1;
